import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import logger from "@/lib/logger";
import { useAuth } from "@/context/AuthContext";

// App-wide presence heartbeat. While the app is open we ping every ~30s so
// the current user shows "online", and we poll the online roster every ~25s.
// Any avatar (via <MiniAvatar userId=…>) reads this to show a green/grey dot.
const PresenceContext = createContext({ onlineIds: new Set(), isOnline: () => false });

const PING_MS = 30000;
const POLL_MS = 25000;

export function PresenceProvider({ children }) {
  const { user } = useAuth();
  const [onlineIds, setOnlineIds] = useState(() => new Set());
  const pingRef = useRef(null);
  const pollRef = useRef(null);

  const ping = useCallback(async () => {
    if (typeof document !== "undefined" && document.hidden) return;
    try { await api.post("/presence/ping"); }
    catch (err) { logger.debug("presence ping failed:", err?.message || err); }
  }, []);

  const poll = useCallback(async () => {
    try {
      const { data } = await api.get("/presence");
      setOnlineIds(new Set(data?.online_ids || []));
    } catch (err) { logger.debug("presence poll failed:", err?.message || err); }
  }, []);

  useEffect(() => {
    if (!user || user === false) return undefined;
    // Immediate ping + poll on mount so dots light up without waiting a cycle.
    ping();
    poll();
    pingRef.current = setInterval(ping, PING_MS);
    pollRef.current = setInterval(poll, POLL_MS);
    const onVisible = () => { if (!document.hidden) { ping(); poll(); } };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(pingRef.current);
      clearInterval(pollRef.current);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [user, ping, poll]);

  const isOnline = useCallback((id) => onlineIds.has(id), [onlineIds]);
  const value = useMemo(() => ({ onlineIds, isOnline }), [onlineIds, isOnline]);
  return <PresenceContext.Provider value={value}>{children}</PresenceContext.Provider>;
}

export const usePresence = () => useContext(PresenceContext);
